import * as React from 'react';
import type {
	IOption,
	ISelectGroupRenderModel,
	SelectOptionValue,
	SelectSelectionMode,
} from '../types';
import { SelectEmptyState } from './SelectEmptyState';
import { CaptionTypography } from '@components/atoms';
import { cn } from '@utils';

const GROUP_SELECT_ALL_TAB_INDEX = -1;

const getGroupSelectAllPressedState = <TValue extends SelectOptionValue, TMetadata>(
	group: ISelectGroupRenderModel<TValue, TMetadata>
): React.AriaAttributes['aria-pressed'] => {
	if (group.indeterminate) {
		return 'mixed';
	}

	return group.selected;
};

interface ISelectGroupedOptionsProps<
	TValue extends SelectOptionValue = SelectOptionValue,
	TMetadata = unknown
> {
	disableGroupSelectAll?: boolean;
	emptyLabel: string;
	enableGroupSelectAll?: boolean;
	groupedOptions: ReadonlyArray<ISelectGroupRenderModel<TValue, TMetadata>>;
	getGroupSelectAllId: (groupValue: string) => string | undefined;
	highlightedGroupSelectAllId?: string;
	onGroupSelect?: (group: ISelectGroupRenderModel<TValue, TMetadata>) => void;
	options: ReadonlyArray<IOption<TValue, TMetadata>>;
	getGroupSelectAllLabel?: (groupLabel: string) => string;
	renderOptionButton: (option: IOption<TValue, TMetadata>, index: number) => React.ReactElement;
	scrollTargetGroupHeaderRef?: React.Ref<HTMLDivElement>;
	scrollTargetIndex: number;
	selectionMode: SelectSelectionMode;
}

export const SelectGroupedOptions = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	disableGroupSelectAll = false,
	emptyLabel,
	enableGroupSelectAll = false,
	groupedOptions,
	getGroupSelectAllId,
	highlightedGroupSelectAllId,
	onGroupSelect,
	options,
	getGroupSelectAllLabel,
	renderOptionButton,
	scrollTargetGroupHeaderRef,
	scrollTargetIndex,
	selectionMode,
}: ISelectGroupedOptionsProps<TValue, TMetadata>): React.ReactElement => {
	const displayedOptionValues = React.useMemo(() => new Set(options.map(option => option.value)), [options]);
	const visibleGroups = React.useMemo(
		() =>
			groupedOptions
				.map(group => ({
					group,
					visibleOptions: group.options.filter(option => displayedOptionValues.has(option.value)),
				}))
				.filter(({ visibleOptions }) => visibleOptions.length > 0),
		[displayedOptionValues, groupedOptions]
	);

	const renderGroupSelectAll = (group: ISelectGroupRenderModel<TValue, TMetadata>): React.ReactNode => {
		if (!enableGroupSelectAll || selectionMode !== 'multiple' || !onGroupSelect || !getGroupSelectAllLabel) {
			return null;
		}
		const groupSelectAllId = getGroupSelectAllId(group.value);
		const isKeyboardHighlighted = groupSelectAllId === highlightedGroupSelectAllId;

		const handleGroupSelectAllKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
			if (event.key !== 'Enter' && event.key !== ' ') {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			onGroupSelect(group);
		};
		const handleGroupSelectAllMouseDown = (event: React.MouseEvent<HTMLButtonElement>): void => {
			event.preventDefault();
		};

		return (
			<button
				aria-disabled={disableGroupSelectAll || group.disabled}
				aria-pressed={getGroupSelectAllPressedState(group)}
				className={cn(
					'rounded-md px-2 py-1 text-xs font-semibold text-primary transition-colors enabled:hover:bg-bgPrimary focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
					isKeyboardHighlighted && 'bg-highlightBorder ring-2 ring-inset ring-highlightSelected',
					group.selected && 'bg-highlightBorder'
				)}
				data-select-group-all
				data-testid="select-group-select-all"
				disabled={disableGroupSelectAll || group.disabled}
				id={groupSelectAllId}
				onClick={() => onGroupSelect(group)}
				onKeyDown={handleGroupSelectAllKeyDown}
				onMouseDown={handleGroupSelectAllMouseDown}
				tabIndex={GROUP_SELECT_ALL_TAB_INDEX}
				type="button"
			>
				{getGroupSelectAllLabel(group.label)}
			</button>
		);
	};

	if (visibleGroups.length === 0) {
		return <SelectEmptyState label={emptyLabel} />;
	}

	let optionIndex = 0;

	return (
		<>
			{visibleGroups.map(({ group, visibleOptions }) => {
				const groupStartIndex = optionIndex;
				const firstEnabledOptionOffset = visibleOptions.findIndex(option => !option.disabled);
				const shouldScrollGroupHeader =
					firstEnabledOptionOffset !== -1 &&
					scrollTargetIndex === groupStartIndex + firstEnabledOptionOffset;
				const renderedOptions = visibleOptions.map(option => {
					const renderedOption = renderOptionButton(option, optionIndex);
					optionIndex += 1;
					return renderedOption;
				});
				return (
					<div
						aria-label={group.label}
						className="space-y-1"
						data-testid="select-group"
						key={group.value}
						role="group"
					>
						<div
							className="flex items-center justify-between gap-2 px-3 pb-1 pt-2"
							data-testid="select-group-header"
							ref={shouldScrollGroupHeader ? scrollTargetGroupHeaderRef : undefined}
							role="presentation"
						>
							<CaptionTypography aria-hidden="true" className="font-semibold text-primary">
								{group.label}
							</CaptionTypography>
							{renderGroupSelectAll(group)}
						</div>
						{renderedOptions}
					</div>
				);
			})}
		</>
	);
};
